import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '@/services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Banknote, CreditCard, Smartphone, QrCode, Loader2, ChevronLeft } from 'lucide-react';

const MethodBtn = ({ testid, icon: Icon, label, onClick }) => (
  <button onClick={onClick} data-testid={testid}
    className="flex flex-col items-center gap-2 p-4 rounded-xl border border-slate-200 hover:border-sspice hover:bg-red-50 transition-colors">
    <Icon className="w-6 h-6 text-sspice" />
    <span className="text-sm font-semibold text-slate-700">{label}</span>
  </button>
);

export default function PaymentDialog({ order, open, onClose, onPaid }) {
  const storeId = order?.store_id;
  const total = Number(order?.total || 0);
  const [settings, setSettings] = useState(null);
  const [method, setMethod] = useState(null);        // cash | card | upi
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState(null);                // {payment_id, image_url, mock, static_fallback}
  const [staticRef, setStaticRef] = useState('');
  const pollRef = useRef(null);

  const clearPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  useEffect(() => {
    if (!open || !storeId) return;
    setMethod(null); setReference(''); setQr(null); setSettings(null); setStaticRef('');
    apiClient.get(`/payments/settings/${storeId}`, { params: { _: Date.now() } })
      .then((r) => setSettings({ upi_mode: 'manual', methods: { cash: true, card: true, upi: true }, ...(r.data || {}) }))
      .catch(() => setSettings({ upi_mode: 'manual', methods: { cash: true, card: true, upi: true } }));
    return clearPoll;
  }, [open, storeId]);

  const finishPaid = useCallback(() => {
    clearPoll();
    toast.success('Payment received');
    onPaid && onPaid();
  }, [onPaid]);

  const recordManual = async (m) => {
    setBusy(true);
    try {
      await apiClient.post('/payments', {
        order_id: order.id, provider: 'manual', method: m, amount: total,
        reference_number: reference || null,
      });
      finishPaid();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record payment'); }
    finally { setBusy(false); }
  };

  const startQR = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post('/payments/qr/create', { order_id: order.id });
      setQr(r.data);
      pollRef.current = setInterval(async () => {
        try {
          const s = await apiClient.get(`/payments/qr/${r.data.payment_id}/status`);
          if (s.data.status === 'PAID') finishPaid();
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate QR'); setMethod(null); }
    finally { setBusy(false); }
  };

  const cancelQR = async () => {
    clearPoll();
    if (qr?.payment_id) { try { await apiClient.post(`/payments/qr/${qr.payment_id}/cancel`); } catch { /* noop */ } }
    setQr(null); setMethod(null);
  };

  const mockConfirm = async () => {
    try { await apiClient.post(`/payments/qr/${qr.payment_id}/mock-confirm`); finishPaid(); }
    catch { toast.error('Simulate payment failed'); }
  };

  const confirmStatic = async () => {
    if (!staticRef.trim() || staticRef.trim().length < 4) {
      toast.error('Enter the UPI reference number from your bank app');
      return;
    }
    setBusy(true);
    try {
      await apiClient.post(`/payments/qr/${qr.payment_id}/confirm-static`, { reference_number: staticRef.trim() });
      finishPaid();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not confirm payment');
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => { cancelQR(); onClose && onClose(); };
  const chooseUPI = () => { setMethod('upi'); if (settings?.upi_mode === 'razorpay_qr') startQR(); };

  const enabled = settings?.methods || { cash: true, card: true, upi: true };
  const qrMode = settings?.upi_mode === 'razorpay_qr';
  const showManualConfirm = method && (method !== 'upi' || !qrMode);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-md" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle>Collect Payment</DialogTitle>
          <DialogDescription className="sr-only">Choose a payment method to collect payment for this order.</DialogDescription>
        </DialogHeader>

        <div className="text-center py-1">
          <p className="text-xs text-slate-500">Order #{order?.order_number}</p>
          <p className="text-3xl font-bold text-slate-900" data-testid="pay-amount">₹{total.toFixed(2)}</p>
          {(order?.customer?.name || order?.customer?.phone) && (
            <p className="text-xs text-slate-500 mt-1" data-testid="pay-customer">
              {order.customer?.name || 'Guest'}{order.customer?.phone ? ` · ${order.customer.phone}` : ''}
            </p>
          )}
        </div>

        {order?.items && order.items.length > 0 && (
          <div className="mb-2 max-h-24 overflow-y-auto text-xs text-slate-600 bg-slate-50 rounded-lg p-2 space-y-0.5" data-testid="pay-order-items">
            {order.items.map((it, i) => (
              <div key={i} className="flex justify-between gap-2">
                <span className="truncate">{it.qty}× {it.menu_item_name}</span>
                <span className="flex-shrink-0 text-slate-400">₹{Number(it.price * it.qty).toFixed(0)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Method selection — wait until settings are loaded to avoid a mode race */}
        {!qr && !method && (
          settings ? (
            <div className="grid grid-cols-3 gap-3" data-testid="pay-method-grid">
              {enabled.cash && <MethodBtn testid="pay-method-cash" icon={Banknote} label="Cash" onClick={() => setMethod('cash')} />}
              {enabled.card && <MethodBtn testid="pay-method-card" icon={CreditCard} label="Card" onClick={() => setMethod('card')} />}
              {enabled.upi && <MethodBtn testid="pay-method-upi" icon={qrMode ? QrCode : Smartphone} label={qrMode ? 'UPI QR' : 'UPI'} onClick={chooseUPI} />}
            </div>
          ) : (
            <div className="py-6 text-center text-slate-400 text-sm flex items-center justify-center gap-2" data-testid="pay-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          )
        )}

        {/* Manual confirm (cash / card / manual UPI) */}
        {showManualConfirm && (
          <div className="space-y-3" data-testid="pay-manual-confirm">
            <p className="text-sm text-slate-600 uppercase tracking-wide">{method} payment</p>
            <Input placeholder="Reference no. (optional)" value={reference}
              onChange={(e) => setReference(e.target.value)} data-testid="pay-reference-input" />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setMethod(null)} data-testid="pay-back-btn"><ChevronLeft className="w-4 h-4" /></Button>
              <Button className="flex-[3] bg-sspice hover:bg-sspice-hover" disabled={busy}
                onClick={() => recordManual(method)} data-testid="pay-confirm-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Mark Paid · ₹${total.toFixed(2)}`}
              </Button>
            </div>
          </div>
        )}

        {/* Transient state while the Dynamic QR is being generated */}
        {method === 'upi' && qrMode && !qr && (
          <div className="py-6 text-center text-slate-400 text-sm flex items-center justify-center gap-2" data-testid="pay-qr-generating"><Loader2 className="w-4 h-4 animate-spin" /> Generating QR…</div>
        )}

        {/* Dynamic QR */}
        {qr && (
          <div className="space-y-3 text-center" data-testid="pay-qr-view">
            {qr.mock && <div className="text-[11px] font-semibold text-amber-600 bg-amber-50 rounded py-1" data-testid="pay-qr-testmode">TEST MODE — QR is simulated</div>}
            {qr.static_fallback && (
              <div className="text-[11px] font-semibold text-blue-700 bg-blue-50 rounded py-1.5 px-2" data-testid="pay-qr-static-badge">
                {qr.image_url
                  ? "Razorpay unavailable — using our Axis Bank UPI QR. Confirm manually after checking your bank app."
                  : "Razorpay unavailable — use our counter QR stand. Confirm manually after checking your bank app."}
              </div>
            )}
            {qr.image_url ? (
              <img src={qr.image_url} alt="Scan to pay" className="w-72 h-72 mx-auto rounded-lg border border-slate-200" data-testid="pay-qr-image" />
            ) : qr.static_fallback ? (
              <div className="py-6 px-4 border-2 border-dashed border-slate-300 rounded-lg text-slate-600 text-sm" data-testid="pay-qr-standee-instructions">
                Ask the customer to scan the QR stand at the counter and pay <span className="font-bold">₹{total.toFixed(2)}</span>, then confirm below once you've verified the payment.
              </div>
            ) : null}

            {qr.static_fallback ? (
              <div className="space-y-2" data-testid="pay-qr-static-confirm">
                <Input
                  placeholder="UPI reference number"
                  value={staticRef}
                  onChange={(e) => setStaticRef(e.target.value)}
                  data-testid="pay-static-ref-input"
                />
                <Button className="w-full bg-sspice hover:bg-sspice-hover" disabled={busy} onClick={confirmStatic} data-testid="pay-static-confirm-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Confirm Paid · ₹${total.toFixed(2)}`}
                </Button>
              </div>
            ) : (
              <p className="text-sm text-slate-500 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Waiting for payment…</p>
            )}

            {qr.mock && <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={mockConfirm} data-testid="pay-qr-simulate-btn">Simulate Customer Payment (TEST)</Button>}
            <Button variant="outline" className="w-full" onClick={cancelQR} data-testid="pay-qr-cancel-btn">Cancel</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
